import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, Renderer2, computed, effect, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StorageMap } from '@ngx-pwa/local-storage';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { CurrentUserService } from 'src/app/core/services/current-user.service';
import { SessionIdleService } from 'src/app/core/services/session-idle.service';
import { ErrorService } from 'src/app/core/services/error.service';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

/** Bootstrap's lg breakpoint, from which the sidebar is always visible. */
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)';

@Component({
  selector: 'szz-container',
  standalone: true,
  templateUrl: './container.component.html',
  styleUrls: ['./container.component.scss'],
  imports: [HeaderComponent, SidebarComponent, RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'closeSidebar()' },
})
export class ContainerComponent implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly currentUser = inject(CurrentUserService);
  private readonly sessionIdle = inject(SessionIdleService);
  private readonly errorService = inject(ErrorService);
  private readonly storage = inject(StorageMap);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly breakpoints = inject(BreakpointObserver);

  protected readonly sidebarOpen = signal(false);

  protected readonly model = computed(() => {
    const profile = this.currentUser.profile();
    return {
      ...profile,
      // Empty until the profile has loaded (or when it failed): the header then shows no name rather
      // than the English placeholder "Name Undefined".
      fullName: profile?.fullName?.trim() ?? '',
      companyCode: profile?.companyCode?.trim() || '',
      isAdmin: profile?.isAdmin ?? false,
    };
  });

  constructor() {
    // Body scroll-lock is a DOM side effect of the sidebar state, so it lives in an
    // effect instead of a property setter — the signal stays a pure state holder.
    effect(() => {
      if (this.sidebarOpen()) {
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        this.renderer.addClass(this.document.body, 'sidebar-open');
      } else {
        this.renderer.removeStyle(this.document.body, 'overflow');
        this.renderer.removeClass(this.document.body, 'sidebar-open');
      }
    });

    // The narrow-screen menu is an overlay that locks the page scroll, so it closes whenever it has done
    // its job or no longer applies: after a navigation, and when the window grows to desktop width
    // (where the menu is always shown and the overlay would stay invisible but scroll-locking).
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeSidebar());
    this.breakpoints.observe(DESKTOP_MEDIA_QUERY)
      .pipe(filter((state) => state.matches), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeSidebar());
  }

  ngOnInit(): void {
    // Stale locally-cached state must not leak into a fresh (possibly re-authenticated)
    // session, so the profile is loaded only after the storage wipe finishes.
    this.storage.clear()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => this.loadProfile(),
        error: (err) => {
          this.errorService.handleError(err);
          this.loadProfile();
        },
      });
  }

  protected toggleSidebar(): void {
    this.sidebarOpen.update((open) => !open);
  }

  protected closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  protected logout(): void {
    this.document.location.href = `/api/auth/logout?returnUrl=${encodeURIComponent(
      this.document.location.href,
    )}`;
  }

  private loadProfile(): void {
    this.currentUser.load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // The idle logout runs on the profile's AutoLogout* settings.
        next: (profile) => this.sessionIdle.start(profile),
        error: (err) => this.errorService.handleError(err),
      });
  }
}
